import React from 'react'
import {
  AUDIT_RESULT,
  AuditReadinessRecord,
  ControlTest,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { AuditBotNotScannedIcon } from 'src/pages/AuditBot/AuditBot.styles'
import {
  CHECK_RESULT_TYPE,
  getCheckResult,
  getCheckResultIcon,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'

export const getRoadmapModalTitle = (name: string): string => {
  return `${name}: Select Controls`
}

export const getAuditReadinessIcon = (
  check: AuditReadinessRecord,
): JSX.Element => {
  if (!check.getControlTestsList().length) return <AuditBotNotScannedIcon />
  const results = check.getControlTestsList().map((test) => test.getResult())
  return getCheckResultIcon(getCheckResult(results), CHECK_RESULT_TYPE.OVERALL)
}

// A control with no tests is 0% scanned, not NaN. Callers today only read this where records is
// non-empty, but that is their invariant to keep, not something this can rely on — and a NaN would
// slip through `< 100` and `=== 0` alike, reading as "up to date" on a control nobody has tested.
export const getPercentScanned = (records: ControlTest[]): number => {
  const total = records.length
  if (total === 0) return 0
  let scanned = 0
  records.forEach((record) => {
    if (record.getResult() !== AUDIT_RESULT.NOT_RUN) scanned++
  })
  return Math.round((scanned / total) * 100)
}

/**
 * A readiness record's tests as the ControlTests the audit-scan accordion renders.
 *
 * The two messages carry the same per-test fields, but the control's identity — name and model id —
 * lives on the parent record rather than on each test, which is why this takes the whole record.
 * The accordion reads both (its copy menu titles each result with them), so a readiness test record
 * handed over directly would render a result with no control on it.
 *
 * sample_date has no counterpart in readiness and is left unset; nothing in the accordion reads it.
 */
export const controlTestsFromReadiness = (
  record: AuditReadinessRecord,
): ControlTest[] =>
  record
    .getControlTestsList()
    .map((test) =>
      new ControlTest()
        .setId(test.getId())
        .setControlId(record.getControlId())
        .setControlModelId(record.getControlModelId())
        .setControlName(record.getControlName())
        .setTestName(test.getTestName())
        .setResult(test.getResult())
        .setReason(test.getReason())
        .setError(test.getError())
        .setIsStale(test.getIsStale())
        .setReasonSource(test.getReasonSource())
        .setAuditRunId(test.getAuditRunId())
        .setUpdatedAt(test.getUpdatedAt())
        .setArtifactId(test.getArtifactId())
        .setOriginalReason(test.getOriginalReason())
        .setOriginalResult(test.getOriginalResult())
        .setReviewedAt(test.getReviewedAt())
        .setReviewedBy(test.getReviewedBy())
        .setEditedAt(test.getEditedAt())
        .setEditedBy(test.getEditedBy()),
    )
